/**
 * How a unit moves from **finding the notes** to **playing them in time**,
 * and how the tempo climbs from there.
 *
 * Pure TS: no React, no Studio, no DOM. Sibling of `practice.ts` (the runs
 * without rhythm) and `rhythm.ts` (the runs in time), and it is the only
 * place that reads the two together.
 *
 * ## What this replaces
 *
 * Three steps and two decisions the player had to make blind:
 *
 * - « en rythme, main gauche » and « en rythme, main droite », two rungs of
 *   a ladder walked before the two hands came together in time. They
 *   decomposed a unit the parcours had already decomposed — S1b is the left
 *   hand alone, S2b the right hand alone — so a two-hand unit was crossed
 *   three times in time to reach the one crossing that teaches the step;
 * - the tempo the run in time opened at, computed from the unit's **note
 *   density** and a hypothesis about what is comfortable
 *   (`TARGET_ATTACKS_PER_SECOND`). A good rule with nothing to measure. Once
 *   the player has actually played the passage there is something to
 *   measure, and it beats any hypothesis: it is *them*;
 * - « ▶ Jouer plus vite », a link the player had to notice and press to
 *   climb a ladder the screen never said they were on.
 *
 * What replaces them is one continuity. The two-hand practice reads how the
 * player is playing — how clean, and how fast — and the tempo it reads is the
 * one the passage meets the metronome at. From then on the tempo is what
 * climbs, one rung per pass that holds.
 *
 * > The player never chooses a tempo. They play, and the tempo follows.
 *
 * ## Who decides *when*
 *
 * Not this file, and it used to. Three even passes crossed the unit into time
 * on their own, and « 4 passages réussis » is what the player saw — a number
 * standing in for « I am comfortable now ». **That is not a fact these
 * intervals contain.** The reading can see that a pass came out evenly; it
 * cannot see that someone feels ready, and how many repetitions it takes to
 * get there is theirs, not a constant.
 *
 * So the reading keeps what it is good at and gives back what it never had.
 * It says what it sees (`readyForTimeLine`), it measures the tempo
 * (`naturalRate`), and the crossing itself is the player's act, in both
 * directions, as often as they want: « Passer en rythme » in, « Retour » out.
 * See `PhraseFSM`, « S3 and S4 are one step in two régimes ».
 *
 * ## Nothing here is a gate
 *
 * A score below the bar costs nothing: the switch into time is drawn from the
 * first pass and never withheld. What the score decides is which of the two
 * acts the card puts forward, which is a recommendation, never a sanction.
 * That is the same arbitration the rest of the parcours makes — see the note
 * on `PhraseFSM` about the gate that was removed — and it is why no number
 * here is ever shown to the player as a score.
 */

import {
  attacksPerSecond,
  honouredRatio,
  openingCeiling,
  playedClean,
  RATE_LADDER,
  type RunReading,
  snapToLadder,
  strayTolerance,
  unitOpeningRate,
  wentThrough,
} from './rhythm';
import type { PracticeGroup } from './practice';

// ── The pass without rhythm ───────────────────────────────────────

/**
 * What a finished pass of a run without rhythm amounted to.
 *
 * `groups` is how many attacks the pass walked, `wrongGroups` how many of
 * them the player struck a key beside on. Both come off the run itself
 * (`usePracticeRun`), which counts a group **once** however many wrong keys
 * were pressed on it: the question is how many places gave trouble, not how
 * long the trouble lasted.
 *
 * `intervals` is the wall-clock gap between consecutive confirmed groups, in
 * milliseconds. It is what the tempo is read from.
 */
export type PracticePass = {
  readonly groups: number;
  readonly wrongGroups: number;
  readonly intervals: readonly number[];
};

/**
 * How much of the pass came out on the first try, in `[0, 1]`.
 *
 * The share of attacks the player reached without striking a key beside. Not
 * a count of wrong keys: one hesitant chord fumbled four times is one place
 * that gave trouble, and counting the presses would make a single sticky
 * moment read like a pass full of errors.
 *
 * **On its own this says almost nothing**, and that is worth stating where it
 * is defined. The step shows the player each attack and waits for it, so
 * being accurate costs only patience: a beginner three seconds into every key
 * scores 1. Accuracy is a necessary condition here and never a sufficient
 * one — see `isFluent`.
 */
export function practiceScore(pass: PracticePass): number {
  if (pass.groups <= 0) {
    return 1;
  }

  return Math.max(0, 1 - pass.wrongGroups / pass.groups);
}

/**
 * Share of a pass that has to come out clean before it counts as fluent.
 *
 * Nine attacks out of ten, the same bar `PLAYED_THROUGH_RATIO` sets for a run
 * in time. On a median unit of 12 attacks it means at most one place that
 * gave trouble.
 */
export const READY_FOR_TIME_SCORE = 0.9;

/**
 * Attacks a pass has to carry before it can be read at all.
 *
 * Four, so the reading rests on at least three intervals: `hesitations`
 * measures each interval against the median of the others, and a median drawn
 * from one or two numbers is not a median. Below it the step never says the
 * passage is installed, which costs nothing — the switch into time is right
 * there, and a three-attack unit has no fluency to install anyway.
 */
const MIN_SCORED_GROUPS = 4;

/**
 * How much longer than the player's own step an interval has to be before it
 * reads as **stopping to think**.
 *
 * Two and a half times the median. The multiple is the whole idea: it is
 * measured against *their* pace, not against a clock, so a slow player who is
 * even is not accused of hesitating, and a fast player who freezes for a
 * second is.
 */
const HESITATION_FACTOR = 2.5;

/** Below this many intervals, a pass gets no hesitation at all. */
const HESITATION_ALLOWANCE_FROM = 5;

/**
 * Places in the pass where the player stopped to look for the key.
 *
 * ## Why this is the measurement, and accuracy was not
 *
 * What a player means by « I am comfortable with it » is not that they hit
 * the right keys — the screen shows them each one and waits. It is that they
 * **no longer have to stop and think**. That is a fact about the *shape* of a
 * pass, and it is visible: someone still hunting plays three quick attacks
 * and then a hole, someone who has installed the passage plays steps of
 * roughly equal length, slow ones perhaps, but equal.
 *
 * The previous bar let a unit cross into time after two passes because both
 * were accurate, on a passage the player needed ten more tries to hold. Every
 * one of those passes was full of holes, and nothing was reading them.
 */
export function hesitations(intervals: readonly number[]): number {
  const median = medianOf(intervals);

  if (median <= 0) {
    return 0;
  }

  return intervals.filter((ms) => ms > median * HESITATION_FACTOR).length;
}

/**
 * Hesitations a pass may carry and still read as fluent.
 *
 * One, past a handful of intervals. A single long step is what a phrase
 * ending, a change of hand position or a breath looks like, and a rule that
 * called those a failure would never let anybody through. Two is hunting.
 *
 * Short runs get none: on four intervals, one hole is a quarter of the pass.
 */
export function hesitationAllowance(intervalCount: number): number {
  return intervalCount >= HESITATION_ALLOWANCE_FROM ? 1 : 0;
}

/**
 * Whether this pass reads as **comfortable**.
 *
 * Three claims, and each one answers a question the others cannot: the
 * passage came out under the right keys (`practiceScore`), it came out
 * without stopping to think (`hesitations`), and there was enough of it to
 * read (`MIN_SCORED_GROUPS`).
 *
 * The tempo is the fourth condition and it does not live here — see
 * `naturalRate`, which answers `null` when the pace the player holds is
 * slower than anything the ladder can offer. A pass can be perfectly even and
 * still be far too slow to meet a metronome, and those are two different
 * things to say.
 */
/**
 * Whether a pass carries enough attacks for the reading to have an opinion.
 *
 * Below `MIN_SCORED_GROUPS` there is no median to measure hesitation against,
 * so `isFluent` answers `false` for want of evidence and not because the pass
 * went badly. Callers that arbitrate on the reading need to tell the two
 * apart: « the passage is not there yet » and « I cannot see » lead to
 * different screens.
 */
export function readablePass(pass: PracticePass): boolean {
  return pass.groups >= MIN_SCORED_GROUPS;
}

export function isFluent(pass: PracticePass): boolean {
  if (pass.groups < MIN_SCORED_GROUPS) {
    return false;
  }

  if (practiceScore(pass) < READY_FOR_TIME_SCORE) {
    return false;
  }

  return (
    hesitations(pass.intervals) <= hesitationAllowance(pass.intervals.length)
  );
}

/**
 * Fluent passes in a row before the step says the passage is installed.
 *
 * The same three as the rung of tempo one step later (`TEMPO_UP_STREAK`), and
 * for the same reason: a passage that comes out once has been *found*, one
 * that comes out three times running has been **installed**. Here the streak
 * is mostly a guard against a lucky pass — `isFluent` does the work.
 *
 * It used to *cross* the unit into time on its own. It no longer crosses
 * anything: three even passes are a fact about the passage, not about the
 * player's readiness, and the number of repetitions it takes to feel ready is
 * theirs. What the streak does now is move the card's primary action onto the
 * régime switch, and put a sentence under it. See `readyForTimeLine`.
 */
export const FLUENT_STREAK = 3;

/**
 * The run of fluent passes after this one.
 *
 * A pass that is not fluent takes it back to zero: what is being built is
 * « I can play this, again, and again », and a pass with holes in it is not
 * part of that claim.
 *
 * Passes that say nothing about the unit — accompanied, or resumed part-way
 * through — never reach here: the caller leaves the streak alone.
 */
export function afterPracticePass(
  streak: number,
  pass: PracticePass,
): number {
  return isFluent(pass) ? streak + 1 : 0;
}

/**
 * Whether the run of fluent passes says the passage is there.
 *
 * A recommendation, and only that: the switch into time is drawn from the
 * first pass and never withheld. What this decides is which of the two acts
 * the card puts forward, and whether it says why.
 */
export function readyForTime(streak: number): boolean {
  return streak >= FLUENT_STREAK;
}

/**
 * What the card says while the run of fluent passes is building, or `null`.
 *
 * It names **régularité** and not speed, because that is what is actually
 * being watched and because it is the true thing to teach: playing it well is
 * playing it evenly, at whatever pace. The player who reads this line and
 * slows down to stop hesitating is doing exactly the right thing.
 *
 * It used to end « et on passe en rythme », which was the screen announcing
 * what it was about to do to the player. It does nothing to them now: the
 * switch is theirs, from the first pass, and this line reports on the
 * passage, not on a countdown to a transition.
 *
 * Only shown when the tempo measured on the pass reaches the ladder — the
 * caller checks. Talking about what comes next in time, to someone whose pace
 * no metronome can match, is the promise this whole reading exists to stop
 * making.
 */
export function fluencyLine(streak: number): string | null {
  const left = FLUENT_STREAK - streak;

  if (streak <= 0 || left <= 0) {
    return null;
  }

  if (left === 1) {
    return 'Encore un passage aussi régulier et tu l’auras installé.';
  }

  return `Encore ${left} passages aussi réguliers et tu l’auras installé.`;
}

// ── What the card says about the pass that just happened ──────────

/**
 * The headline of the card that closes a run without rhythm.
 *
 * ## Why « Passage réussi. » could not stay
 *
 * It was the headline of **every** pass, and on these screens that is not a
 * claim, it is a tautology: the `SequenceWatcher` waits for the right key, so
 * a pass that ends is a pass that walked every attack, whatever it cost. A
 * player who spent a minute hunting, stopping at every second note, was told
 * the passage was a success in the same words as one who had it under the
 * fingers.
 *
 * That is worse than flattery. This step's whole job is to separate « I found
 * the notes » from « I have them » — see `hesitations` — and the one screen
 * that reports on a pass was rounding both to the same sentence, while the
 * reading that *can* tell them apart was computed and thrown away.
 *
 * Three headlines, and each claims exactly what the reading behind it can
 * support. `isFluent` measures **evenness**, not speed — a player at one
 * attack every three seconds can be perfectly even — so the headline it earns
 * says the passage came out without stopping, and does not say the passage is
 * *there*. That claim needs a tempo, which lives one line down. The middle
 * headline is the honest reading of a hunting pass: the notes were found.
 * Which is true, is not nothing, and is not the same as having them.
 */
export function practiceTitle(pass: PracticePass | null): string {
  if (pass === null || !readablePass(pass)) {
    return 'Passage réussi.';
  }

  if (isFluent(pass)) {
    return 'D’un bout à l’autre, sans t’arrêter.';
  }

  return 'Tu as trouvé toutes les notes.';
}

/**
 * The holes in the pass, named, or `null` when there were none worth naming.
 *
 * The measurement that decides everything on this step, said out loud for the
 * first time. It was computed on every pass and shown on none, so a player
 * hunting for keys read « Recommence pour le mémoriser » — advice for someone
 * who already has the passage — and had no way to know what the screen was
 * actually waiting for.
 *
 * Naming the hand and not the player is not a nicety: « tu t'es arrêté »
 * forces an agreement the application has no business guessing, and the hand
 * is what the reading is about anyway.
 *
 * The advice attached to it is the true one, and it is counter-intuitive
 * enough to be worth stating: the way out of hesitation is **slower**, not
 * more careful. A player who reads this and drops their pace until the steps
 * are even is doing exactly the right thing.
 */
export function hesitationLine(pass: PracticePass): string | null {
  const count = hesitations(pass.intervals);

  if (count <= hesitationAllowance(pass.intervals.length)) {
    return null;
  }

  const holes =
    count === 1
      ? 'La main s’est arrêtée une fois pour chercher.'
      : `La main s’est arrêtée ${count} fois pour chercher.`;

  return `${holes} Reprends plus lentement, sans t’arrêter.`;
}

/**
 * The places that took more than one try, or `null` when the pass was clean.
 *
 * Ranked under `hesitationLine` by the caller, and the order is the point: on
 * a screen that waits for the right key, striking a neighbour costs a second
 * and teaches the hand where the key is. Stopping to *look* is the thing that
 * says the passage is not installed. Both are worth reporting; only one of
 * them is what this step is about.
 */
export function stumbleLine(pass: PracticePass): string | null {
  if (pass.wrongGroups === 0) {
    return null;
  }

  if (pass.wrongGroups === 1) {
    return 'Une attaque a demandé plusieurs essais.';
  }

  return `${pass.wrongGroups} attaques ont demandé plusieurs essais.`;
}

/**
 * What is said to a player who is perfectly even and slower than the ladder.
 *
 * `naturalRate` answers `null` there, and the step used to answer nothing:
 * the fluency line is gated on a tempo existing, so the one player whose
 * passes are exactly what the step asks for was the one told least. They kept
 * repeating a passage they had, with no idea what was missing.
 *
 * What is missing is not theirs to fix by being more careful, so the sentence
 * says the true thing and stops: it is regular, and the metronome cannot go
 * that slow. Repetition is what closes that gap, and repetition is what the
 * card is already offering.
 */
export function steadyButSlowLine(): string {
  return 'C’est régulier, mais encore trop lent pour que le métronome suive.';
}

/**
 * What the card says once the passes are coming out evenly.
 *
 * The reading, stated as what it is: an observation the player is free to act
 * on or ignore. It replaces the transition this streak used to trigger, and
 * that is the whole trade — the machine keeps the part it is good at, saying
 * what it sees and measuring a tempo, and gives back the part it never had,
 * knowing whether someone feels ready.
 *
 * `bpm` is the tempo the timed régime would open at, when there is one to
 * name. Naming it is what makes the offer answerable: « en rythme » is an
 * abstraction, « à 54 BPM » is something a player can picture refusing.
 */
export function readyForTimeLine(bpm: number | null): string {
  const seen = `Tes ${FLUENT_STREAK} derniers passages sont réguliers.`;

  if (bpm === null) {
    return seen;
  }

  return `${seen} En rythme, ça partirait à ${bpm} BPM.`;
}

// ── The tempo the player is already playing at ────────────────────

/**
 * Attacks per second the player sustained during a pass without rhythm, or
 * `0` when the pass is too short to hold a rate.
 *
 * The **median** interval and not the mean, and this is the whole robustness
 * of the measurement: a run without rhythm is a run where the player stops to
 * find a key, reads ahead, takes a chord one finger at a time. Those pauses
 * are not their tempo, they are the discovery, and a mean would carry every
 * one of them. The median asks a different question — what does the ordinary
 * step from one attack to the next cost this player — and that is the one
 * the run in time is about to ask of them.
 */
export function playedAttacksPerSecond(intervals: readonly number[]): number {
  const median = medianOf(intervals);

  if (median <= 0) {
    return 0;
  }

  return 1000 / median;
}

/** Median of the positive intervals, or `0` when there are none. */
function medianOf(intervals: readonly number[]): number {
  const usable = intervals.filter((ms) => ms > 0);

  if (usable.length === 0) {
    return 0;
  }

  const sorted = [...usable].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
}

/**
 * How far under the slowest rung a measured pace may sit and still be snapped
 * up to it.
 *
 * Snapping is rounding, and rounding 0.34 up to 0.35 is what a ladder is for.
 * Rounding **0.15** up to 0.35 is not rounding, it is inventing a tempo more
 * than twice what the player holds and calling it theirs — which is exactly
 * what happened: a passage the player needed ten more tries on was handed a
 * metronome at 42 BPM, announced as « à ton tempo », and it fell apart on the
 * first pass. Past this margin there is no rung, and saying so is the answer.
 */
const LADDER_REACH = 0.85;

/**
 * The tempo the run in time should open at, read off a pass without rhythm.
 *
 * The conversion is the one `startingRate` already does, with the hypothesis
 * taken out of it: instead of aiming at two attacks per second because two is
 * a reasonable number, it aims at the number of attacks per second the player
 * has just sustained. The unit's own density does the rest — a dense passage
 * needs a lower fraction of the written tempo to come out at the same rate of
 * attacks, and that is exactly what makes a fraction the wrong thing to fix
 * globally.
 *
 * Returns `null` in two cases, and they mean different things:
 *
 * - **nothing to read** — too few intervals, a unit with no span, a tempo of
 *   zero. The run in time falls back to the density rule;
 * - **nothing on the ladder** — the player holds a pace slower than the
 *   slowest rung by more than `LADDER_REACH`. The unit does **not** cross into
 *   time on its own: there is no tempo to offer, and offering the floor
 *   anyway is the deception this guard exists to prevent.
 *
 * The caller cannot tell the two apart, and does not need to: neither one is
 * a tempo, so neither one crosses.
 */
export function naturalRate(
  groups: readonly PracticeGroup[],
  bpm: number,
  ppq: number,
  intervals: readonly number[],
): number | null {
  const played = playedAttacksPerSecond(intervals);

  if (played <= 0) {
    return null;
  }

  const atFullSpeed = attacksPerSecond(groups, bpm, ppq, 1);

  if (atFullSpeed <= 0) {
    return null;
  }

  const raw = played / atFullSpeed;

  if (raw < RATE_LADDER[0] * LADDER_REACH) {
    return null;
  }

  // The same ceiling `startingRate` applies, arriving by the other door and
  // for the same reason: finding the notes of a passage quickly is not
  // playing it in time at the written tempo. See `openingCeiling`, which
  // also keeps a slow piece from being held under a tempo felt as a pulse.
  return Math.min(snapToLadder(raw), openingCeiling(bpm));
}

/**
 * The tempo a run in time opens at, from what is known about the unit.
 *
 * Three sources, in this order, and the order is the point:
 *
 * 1. **the pass that just happened**, when the player has come straight from
 *    the practice run. Nothing knows better what this passage costs this
 *    player today;
 * 2. **what the unit has held before**, one notch below its record — a record
 *    says what it cost once, not what it costs now. See `unitOpeningRate`;
 * 3. **the unit's density**, the rule that applies when nobody has played it
 *    yet.
 *
 * `maxRate` caps the result and is phase 2's: a unit taken up again inside a
 * piece is taken up *because the piece does not hold there*, so reworking it
 * above the tempo of the thing it is part of would train the wrong speed.
 */
export function openingRateInTime(
  pace: number | null,
  held: number | null,
  groups: readonly PracticeGroup[],
  bpm: number,
  ppq: number,
  maxRate?: number,
): number {
  const own =
    pace !== null && pace > 0 ? pace : unitOpeningRate(held, groups, bpm, ppq);

  return maxRate === undefined ? own : Math.min(own, maxRate);
}

// ── The pass in time ──────────────────────────────────────────────

/**
 * How a pass in time went, in `[0, 1]`.
 *
 * Three facts, and each one can sink it on its own, because each answers a
 * question the next cannot:
 *
 * - **stopping** is the one adverse event of the step. A pass that stopped
 *   scores zero however good the rest of it was: what the step teaches is
 *   that the hands do not stop, and a passage crossed in two halves was not
 *   crossed;
 * - **the attacks answered** (`honouredRatio`) says the passage was played
 *   rather than skated over;
 * - **the keys beside** are taken off it, scaled by the tolerance a run of
 *   this length already gets (`strayTolerance`). One key caught on the way
 *   past is what learning a passage sounds like and costs a fraction of a
 *   rung; a pass full of wrong notes cannot climb.
 *
 * Never shown. It decides whether the tempo goes up, and the player reads
 * that in the tempo, which is the only honest form of it.
 */
export function runScore(reading: RunReading): number {
  if (!wentThrough(reading)) {
    return 0;
  }

  const onsets = reading.honoured.length;

  if (onsets === 0) {
    return 0;
  }

  const beside = Math.max(
    0,
    reading.strays.length - strayTolerance(onsets),
  );

  return Math.max(0, honouredRatio(reading) - beside / onsets);
}

/**
 * Score a pass in time has to reach before the tempo goes up on its own.
 *
 * At `PLAYED_THROUGH_RATIO`, deliberately: the two say the same thing about
 * the same pass, so a passage that reads as played is a passage that climbs.
 * A separate, higher bar would produce the one state this step must not have
 * — a pass the screen calls a crossing and the tempo silently refuses to
 * reward.
 */
export const TEMPO_UP_SCORE = 0.9;

/**
 * Whether this pass earns the next rung.
 *
 * `playedClean` is asked again on top of the score, and it is not redundant:
 * the score subtracts strays *above* the tolerance, so a run sitting exactly
 * at its tolerance still scores full marks. That tolerance exists to keep a
 * crossing from being called a failure; it was never meant to certify that
 * the passage came out under the right keys, which is the claim going faster
 * makes.
 */
export function earnsFasterTempo(reading: RunReading): boolean {
  return runScore(reading) >= TEMPO_UP_SCORE && playedClean(reading);
}

/**
 * Passes that have to hold **in a row** before the tempo moves up a rung.
 *
 * One was not enough, and the reason is what the step is for. A passage that
 * comes out once has been *found*; a passage that comes out three times
 * running has been **installed**, and installing it is the whole content of
 * the repetition this screen is built around (`PASS_DOTS`). Climbing on the
 * first clean crossing put the player a rung up on the strength of a pass
 * they might not be able to repeat, and four repetitions took them four rungs
 * from where they could actually play.
 *
 * Three and not four: four is the dosage of the *card* — how many times a
 * passage is worth repeating before moving on — and borrowing it here would
 * make the two counters the same number for two different questions, with the
 * player unable to ever reach the fourth repetition at a settled tempo.
 */
export const TEMPO_UP_STREAK = 3;

/**
 * The streak after a pass in time.
 *
 * Anything that does not earn a rung takes the streak back to zero: the claim
 * being built is « I can play this, again, and again », and a pass that
 * stopped or came out under the wrong keys is not part of it.
 *
 * A pass over **part** of the unit never reaches here — the caller returns
 * before it. It neither counts nor breaks: it is a piece of the passage, and
 * the streak is about the passage.
 */
export function afterPassInTime(streak: number, reading: RunReading): number {
  return earnsFasterTempo(reading) ? streak + 1 : 0;
}

/** Whether the streak has earned the next rung. */
export function earnsNextRung(streak: number): boolean {
  return streak >= TEMPO_UP_STREAK;
}

/**
 * What the card says while the streak is building, or `null`.
 *
 * The streak is not drawn — no counter, no score — but it must not be silent
 * either: a tempo that moves every third pass with nothing said about it is a
 * screen doing something behind the player's back. One sentence, naming what
 * is left, and it only appears on a pass that actually earned its place in
 * the streak.
 */
export function streakLine(streak: number): string | null {
  const left = TEMPO_UP_STREAK - streak;

  if (streak <= 0 || left <= 0) {
    return null;
  }

  if (left === 1) {
    return 'Encore un passage comme celui-là et le tempo monte.';
  }

  return `Encore ${left} passages comme celui-là et le tempo monte.`;
}

// ── Resuming where the player scrolled ────────────────────────────

/**
 * The attack nearest `tick`, as an index into `groups`.
 *
 * What a scroll of the roll means in a run without rhythm: the player put an
 * attack under the playhead and the run resumes there. Nearest and not
 * next — scrolling onto a note and landing on the one after it is the kind of
 * off-by-one a player reads as the control being broken.
 *
 * Ties go to the earlier attack, which is the forgiving direction: it replays
 * one note rather than skipping one.
 */
export function groupIndexAtTick(
  groups: readonly PracticeGroup[],
  tick: number,
): number {
  if (groups.length === 0) {
    return 0;
  }

  let best = 0;
  let bestDistance = Math.abs(groups[0].startTick - tick);

  for (let i = 1; i < groups.length; i += 1) {
    const distance = Math.abs(groups[i].startTick - tick);

    if (distance < bestDistance) {
      best = i;
      bestDistance = distance;
    }
  }

  return best;
}

/**
 * Where a pass **in time** resumes when the player scrolls the roll: the bar
 * line at or before `tick`, or `null` for the unit's own start.
 *
 * A bar and not the nearest attack, and the difference is the count-in. A run
 * in time is entered on a downbeat — that is what the count-in counts to —
 * so resuming in the middle of a bar would hand the player a departure that
 * contradicts the metre they are about to play. Bars are also the unit the
 * screen already speaks in: the band counts « mesure 3 / 5 » throughout the
 * pass.
 *
 * `null` for the first bar, since resuming there is playing the unit, and for
 * a unit whose start is not on a bar line — a phrase may open on an upbeat,
 * and a "bar 3" measured from an offset grid points at the wrong place. The
 * same rule `barOfTick` follows, for the same reason.
 */
export function resumeBarTick(
  tick: number,
  unitStartTick: number,
  unitEndTick: number,
  ticksPerBar: number,
): number | null {
  if (ticksPerBar <= 0 || unitStartTick % ticksPerBar !== 0) {
    return null;
  }

  if (tick <= unitStartTick || tick >= unitEndTick) {
    return null;
  }

  const bars = Math.floor((tick - unitStartTick) / ticksPerBar);

  if (bars <= 0) {
    return null;
  }

  const resume = unitStartTick + bars * ticksPerBar;

  // A resume point with nothing left after it is not a resume point: the
  // range runner needs a span, and the player asked to play something.
  return resume >= unitEndTick ? null : resume;
}
